import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  LogOut,
  Users,
  Map,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Search,
  Shield,
  DollarSign,
  Activity,
  Loader2,
  MessageSquare,
  Heart,
  Crown,
  MapPin,
  AlertTriangle,
  Megaphone,
  HandHeart,
  BarChart3,
  Eye,
  UserPlus,
  ThumbsUp,
  ThumbsDown,
  Globe,
  Compass
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

// Simple Bar Chart Component
const SimpleBarChart = ({ data, dataKey, labelKey, color = 'teal', height = 200 }) => {
  if (!data || data.length === 0) return <div className="text-gray-400 text-center py-8">No data available</div>;
  
  const maxValue = Math.max(...data.map(d => d[dataKey] || 0));
  
  return (
    <div className="flex items-end justify-between gap-2" style={{ height }}>
      {data.map((item, index) => {
        const value = item[dataKey] || 0;
        const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
        return (
          <div key={index} className="flex-1 flex flex-col items-center gap-2">
            <span className="text-xs font-semibold text-gray-700">{value}</span>
            <div 
              className={`w-full rounded-t-lg transition-all duration-500 ${
                color === 'teal' ? 'bg-gradient-to-t from-teal-500 to-teal-400' :
                color === 'cyan' ? 'bg-gradient-to-t from-cyan-500 to-cyan-400' :
                color === 'amber' ? 'bg-gradient-to-t from-amber-500 to-amber-400' :
                'bg-gradient-to-t from-emerald-500 to-emerald-400'
              }`}
              style={{ height: `${Math.max(percentage, 5)}%`, minHeight: '8px' }}
            />
            <span className="text-xs text-gray-500 truncate w-full text-center">
              {item[labelKey]?.slice(5) || item[labelKey]}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Horizontal Bar Component for rankings
const HorizontalBar = ({ label, value, maxValue, color = 'teal', rank }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-500 w-6">{rank}.</span>
      <div className="flex-1">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700 truncate">{label}</span>
          <span className="text-sm font-bold text-gray-900">{value}</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              color === 'teal' ? 'bg-teal-500' :
              color === 'cyan' ? 'bg-cyan-500' :
              color === 'amber' ? 'bg-amber-500' :
              'bg-emerald-500'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color, change, up, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="p-5 bg-white/80 backdrop-blur border border-gray-100 shadow"
    style={{ borderRadius: '1.25rem' }}
  >
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 flex items-center justify-center ${
        color === 'teal' ? 'bg-teal-100' :
        color === 'cyan' ? 'bg-cyan-100' :
        color === 'amber' ? 'bg-amber-100' :
        color === 'emerald' ? 'bg-emerald-100' :
        color === 'purple' ? 'bg-purple-100' : 'bg-rose-100'
      }`} style={{ borderRadius: '0.75rem' }}>
        <Icon size={20} className={
          color === 'teal' ? 'text-teal-600' :
          color === 'cyan' ? 'text-cyan-600' :
          color === 'amber' ? 'text-amber-600' :
          color === 'emerald' ? 'text-emerald-600' :
          color === 'purple' ? 'text-purple-600' : 'text-rose-600'
        } />
      </div>
      {change && (
        <span className={`flex items-center gap-1 text-xs font-semibold ${up ? 'text-emerald-600' : 'text-red-600'}`}>
          {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {change}
        </span>
      )}
    </div>
    <p className="text-2xl font-black text-gray-900">{value}</p>
    <p className="text-sm text-gray-500">{label}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </motion.div>
);

export default function Admin() {
  const navigate = useNavigate();
  
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [communityStats, setCommunityStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  
  useEffect(() => {
    const token = localStorage.getItem('panchi_admin_token');
    if (!token) {
      navigate('/admin-login');
      return;
    }
    loadDashboard();
  }, [navigate]);
  
  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [dashData, usersData, communityData, analyticsData] = await Promise.all([
        api.getAdminDashboard(),
        api.getAdminUsers(50),
        api.getAdminCommunityStats(),
        api.getAdminAnalytics(),
      ]);
      
      setDashboard(dashData.stats || { total_users: 0, active_users_today: 0, total_plans_generated: 0, plans_today: 0, pro_subscribers: 0, revenue_this_month: 0 });
      setUsers(usersData.users || []);
      setCommunityStats(communityData.stats || { total_members: 0, active_members_30d: 0, total_posts: 0, active_posts: 0 });
      setAnalytics(analyticsData.analytics || { overview: {}, daily_active_users: [], daily_plans: [], plans: {}, page_views: {}, feedback: {}, demographics: {}, top_destinations: [] });
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load dashboard data. Showing live data only.');
      // Set empty states instead of mock data
      setDashboard({ total_users: 0, active_users_today: 0, total_plans_generated: 0, plans_today: 0, pro_subscribers: 0, revenue_this_month: 0 });
      setUsers([]);
      setCommunityStats({ total_members: 0, active_members_30d: 0, total_posts: 0, active_posts: 0 });
      setAnalytics({ overview: {}, daily_active_users: [], daily_plans: [], plans: {}, page_views: {}, feedback: {}, demographics: {}, top_destinations: [] });
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('panchi_admin_token');
    toast.success('Logged out successfully');
    navigate('/admin-login');
  };
  
  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-slate-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              style={{ borderRadius: '50%' }}
              data-testid="back-home"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Shield size={20} className="text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-xs text-gray-500">Panchi Management</p>
              </div>
            </div>
          </div>
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 font-medium transition-colors"
            style={{ borderRadius: '9999px' }}
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6 overflow-x-hidden">
        {/* Tabs */}
        <div className="flex items-center gap-2 p-1 bg-gray-100 overflow-x-auto scrollbar-hide" style={{ borderRadius: '9999px' }}>
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'events', label: 'IPL & Events', icon: Globe },
            { id: 'community', label: 'Community', icon: Users },
            { id: 'users', label: 'Users', icon: Shield },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 font-medium transition-all whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                activeTab === tab.id
                  ? 'bg-white text-slate-700 shadow-sm'
                  : 'text-gray-600 hover:text-slate-700'
              }`}
              style={{ borderRadius: '9999px' }}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>
        
        {/* Overview Tab */}
        {activeTab === 'overview' && dashboard && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard label="Total Users" value={dashboard.total_users?.toLocaleString() || '0'} icon={Users} color="teal" change="+12%" up={true} />
              <StatCard label="Active Today" value={dashboard.active_users_today?.toLocaleString() || '0'} icon={Activity} color="cyan" change="+8%" up={true} />
              <StatCard label="Plans Generated" value={dashboard.total_plans_generated?.toLocaleString() || '0'} icon={Map} color="amber" change="+23%" up={true} />
              <StatCard label="Plans Today" value={dashboard.plans_today?.toLocaleString() || '0'} icon={TrendingUp} color="emerald" change="+5%" up={true} />
              <StatCard label="Pro Subscribers" value={dashboard.pro_subscribers?.toLocaleString() || '0'} icon={Crown} color="purple" change="+18%" up={true} />
              <StatCard label="Revenue (Month)" value={`₹${(dashboard.revenue_this_month || 0).toLocaleString()}`} icon={DollarSign} color="rose" change="+15%" up={true} />
            </div>
            
            {/* Quick Actions */}
            <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => setActiveTab('analytics')}
                  className="flex items-center gap-2 px-5 py-2.5 bg-teal-100 text-teal-700 font-semibold hover:bg-teal-200 transition-all" 
                  style={{ borderRadius: '9999px' }}
                  data-testid="view-analytics-btn"
                >
                  <BarChart3 size={18} />
                  View Analytics
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 transition-all" style={{ borderRadius: '9999px' }}>
                  <Download size={18} />
                  Export Users
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 transition-all" style={{ borderRadius: '9999px' }}>
                  <Download size={18} />
                  Export Plans
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Top Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard 
                label="Total Users" 
                value={analytics.overview?.total_users?.toLocaleString() || '0'} 
                icon={Users} 
                color="teal"
                subtitle={`+${analytics.overview?.new_users_today || 0} today`}
              />
              <StatCard 
                label="Page Views" 
                value={analytics.page_views?.total?.toLocaleString() || '0'} 
                icon={Eye} 
                color="cyan"
                subtitle={`${analytics.page_views?.today || 0} today`}
              />
              <StatCard 
                label="Total Plans" 
                value={analytics.plans?.total?.toLocaleString() || '0'} 
                icon={Map} 
                color="amber"
                subtitle={`${analytics.plans?.avg_per_user || 0} avg/user`}
              />
              <StatCard 
                label="Pro Conversion" 
                value={`${analytics.overview?.conversion_rate || 0}%`} 
                icon={Crown} 
                color="purple"
                subtitle={`${analytics.overview?.pro_users || 0} Pro users`}
              />
            </div>
            
            {/* Charts Row */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Daily Active Users Chart */}
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900">Daily Active Users</h3>
                  <span className="text-sm text-gray-500">Last 7 days</span>
                </div>
                <SimpleBarChart 
                  data={analytics.daily_active_users || []} 
                  dataKey="users" 
                  labelKey="date"
                  color="teal"
                  height={180}
                />
              </div>
              
              {/* Daily Plans Chart */}
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900">Plans Generated</h3>
                  <span className="text-sm text-gray-500">Last 7 days</span>
                </div>
                <SimpleBarChart 
                  data={analytics.daily_plans || []} 
                  dataKey="plans" 
                  labelKey="date"
                  color="amber"
                  height={180}
                />
              </div>
            </div>
            
            {/* User Growth & Feedback Row */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* New Users Stats */}
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
                <h3 className="text-lg font-bold text-gray-900 mb-4">User Growth</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-teal-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <UserPlus size={20} className="text-teal-600" />
                      <span className="text-gray-700">Today</span>
                    </div>
                    <span className="text-xl font-bold text-teal-600">+{analytics.overview?.new_users_today || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-cyan-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <UserPlus size={20} className="text-cyan-600" />
                      <span className="text-gray-700">This Week</span>
                    </div>
                    <span className="text-xl font-bold text-cyan-600">+{analytics.overview?.new_users_week || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <UserPlus size={20} className="text-amber-600" />
                      <span className="text-gray-700">This Month</span>
                    </div>
                    <span className="text-xl font-bold text-amber-600">+{analytics.overview?.new_users_month || 0}</span>
                  </div>
                </div>
              </div>
              
              {/* Feedback Stats */}
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
                <h3 className="text-lg font-bold text-gray-900 mb-4">User Feedback</h3>
                <div className="space-y-4">
                  <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl">
                    <p className="text-4xl font-black text-emerald-600">{analytics.feedback?.satisfaction_rate || 0}%</p>
                    <p className="text-sm text-gray-600">Satisfaction Rate</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl">
                      <ThumbsUp size={18} className="text-green-600" />
                      <div>
                        <p className="text-lg font-bold text-green-600">{analytics.feedback?.positive || 0}</p>
                        <p className="text-xs text-gray-500">Positive</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl">
                      <ThumbsDown size={18} className="text-red-600" />
                      <div>
                        <p className="text-lg font-bold text-red-600">{analytics.feedback?.negative || 0}</p>
                        <p className="text-xs text-gray-500">Negative</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Onboarding & Auth */}
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Demographics</h3>
                <div className="space-y-4">
                  <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl">
                    <p className="text-4xl font-black text-purple-600">{analytics.demographics?.onboarding_rate || 0}%</p>
                    <p className="text-sm text-gray-600">Completed Onboarding</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Auth Methods</p>
                    {(analytics.demographics?.auth_methods || []).map((auth, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 text-gray-600">
                          <Globe size={14} />
                          {auth.method === 'google' ? 'Google' : auth.method === 'email' ? 'Email' : auth.method}
                        </span>
                        <span className="font-semibold text-gray-900">{auth.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Top Destinations & Travel Styles */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Top Destinations */}
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin size={20} className="text-teal-600" />
                  <h3 className="text-lg font-bold text-gray-900">Top Destinations</h3>
                </div>
                <div className="space-y-3">
                  {(analytics.top_destinations || []).slice(0, 5).map((dest, idx) => (
                    <HorizontalBar 
                      key={idx}
                      label={dest.destination}
                      value={dest.count}
                      maxValue={(analytics.top_destinations || [])[0]?.count || 1}
                      color="teal"
                      rank={idx + 1}
                    />
                  ))}
                  {(!analytics.top_destinations || analytics.top_destinations.length === 0) && (
                    <p className="text-gray-400 text-center py-4">No destination data yet</p>
                  )}
                </div>
              </div>
              
              {/* Travel Style Preferences */}
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Compass size={20} className="text-amber-600" />
                  <h3 className="text-lg font-bold text-gray-900">Travel Styles</h3>
                </div>
                <div className="space-y-3">
                  {(analytics.demographics?.travel_styles || []).slice(0, 5).map((style, idx) => (
                    <HorizontalBar 
                      key={idx}
                      label={style.style}
                      value={style.count}
                      maxValue={(analytics.demographics?.travel_styles || [])[0]?.count || 1}
                      color="amber"
                      rank={idx + 1}
                    />
                  ))}
                  {(!analytics.demographics?.travel_styles || analytics.demographics.travel_styles.length === 0) && (
                    <p className="text-gray-400 text-center py-4">No travel style data yet</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Page Views & Errors */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Top Pages */}
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Eye size={20} className="text-cyan-600" />
                  <h3 className="text-lg font-bold text-gray-900">Page Views</h3>
                </div>
                <div className="space-y-3">
                  {(analytics.page_views?.by_page || []).slice(0, 5).map((page, idx) => (
                    <HorizontalBar 
                      key={idx}
                      label={page.page === '/' ? 'Home' : page.page}
                      value={page.views}
                      maxValue={(analytics.page_views?.by_page || [])[0]?.views || 1}
                      color="cyan"
                      rank={idx + 1}
                    />
                  ))}
                  {(!analytics.page_views?.by_page || analytics.page_views.by_page.length === 0) && (
                    <p className="text-gray-400 text-center py-4">No page view data yet</p>
                  )}
                </div>
              </div>
              
              {/* Recent Errors */}
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle size={20} className="text-red-600" />
                  <h3 className="text-lg font-bold text-gray-900">Recent Errors</h3>
                </div>
                {(analytics.recent_errors || []).length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {analytics.recent_errors.map((err, idx) => (
                      <div key={idx} className="p-3 bg-red-50 rounded-lg text-sm">
                        <p className="font-medium text-red-800">{err.message || err.error || 'Unknown error'}</p>
                        <p className="text-red-600 text-xs mt-1">{err.timestamp || 'No timestamp'}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Shield size={32} className="text-green-600" />
                    </div>
                    <p className="text-green-600 font-medium">No errors logged</p>
                    <p className="text-gray-400 text-sm">System running smoothly</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Export Actions */}
            <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Export Analytics</h3>
              <div className="flex flex-wrap gap-3">
                <button className="flex items-center gap-2 px-5 py-2.5 bg-teal-100 text-teal-700 font-semibold hover:bg-teal-200 transition-all" style={{ borderRadius: '9999px' }}>
                  <Download size={18} />
                  Export User Data (CSV)
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 bg-amber-100 text-amber-700 font-semibold hover:bg-amber-200 transition-all" style={{ borderRadius: '9999px' }}>
                  <Download size={18} />
                  Export Plans Data (CSV)
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 bg-cyan-100 text-cyan-700 font-semibold hover:bg-cyan-200 transition-all" style={{ borderRadius: '9999px' }}>
                  <Download size={18} />
                  Export Analytics Report
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Events Tab - IPL 2026 & More */}
        {activeTab === 'events' && (
          <div className="space-y-6">
            {/* IPL 2026 Banner */}
            <div className="p-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-lg" style={{ borderRadius: '1.25rem' }}>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-2xl font-black mb-1">TATA IPL 2026</h2>
                  <p className="text-white/80">March 28 - May 24, 2026 | 70 Matches | 10 Teams | 13 Venues</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-center px-4 py-2 bg-white/20 backdrop-blur rounded-xl">
                    <p className="text-2xl font-bold">70</p>
                    <p className="text-xs text-white/80">Matches</p>
                  </div>
                  <div className="text-center px-4 py-2 bg-white/20 backdrop-blur rounded-xl">
                    <p className="text-2xl font-bold">10</p>
                    <p className="text-xs text-white/80">Teams</p>
                  </div>
                  <div className="text-center px-4 py-2 bg-white/20 backdrop-blur rounded-xl">
                    <p className="text-2xl font-bold">13</p>
                    <p className="text-xs text-white/80">Venues</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* IPL Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Cities Covered" value="13" icon={MapPin} color="teal" subtitle="Including Dharamshala & Raipur" />
              <StatCard label="Opening Match" value="Mar 28" icon={Activity} color="purple" subtitle="RCB vs SRH @ Bengaluru" />
              <StatCard label="Final Match" value="May 24" icon={Crown} color="amber" subtitle="Grand Finale" />
              <StatCard label="Double Headers" value="14" icon={TrendingUp} color="cyan" subtitle="Weekend specials" />
            </div>
            
            {/* IPL Venues */}
            <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Globe size={20} className="text-teal-600" />
                  <h3 className="text-lg font-bold text-gray-900">IPL 2026 Venues</h3>
                </div>
                <span className="text-sm text-gray-500">Matches per venue</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { city: 'Mumbai', matches: 8, stadium: 'Wankhede Stadium' },
                  { city: 'Chennai', matches: 8, stadium: 'MA Chidambaram' },
                  { city: 'Kolkata', matches: 8, stadium: 'Eden Gardens' },
                  { city: 'Bengaluru', matches: 6, stadium: 'Chinnaswamy' },
                  { city: 'Hyderabad', matches: 7, stadium: 'Rajiv Gandhi' },
                  { city: 'Delhi', matches: 7, stadium: 'Arun Jaitley' },
                  { city: 'Ahmedabad', matches: 7, stadium: 'Narendra Modi' },
                  { city: 'Lucknow', matches: 7, stadium: 'Ekana' },
                  { city: 'Jaipur', matches: 4, stadium: 'Sawai Mansingh' },
                  { city: 'Mohali', matches: 5, stadium: 'PCA Stadium' },
                  { city: 'Guwahati', matches: 3, stadium: 'Barsapara' },
                  { city: 'Dharamshala', matches: 3, stadium: 'HPCA' },
                ].map((venue, idx) => (
                  <div key={idx} className="p-3 bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:shadow-md transition-all" style={{ borderRadius: '0.75rem' }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-gray-900">{venue.city}</span>
                      <span className="text-sm font-bold text-teal-600">{venue.matches}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate">{venue.stadium}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Teams Section */}
            <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
              <div className="flex items-center gap-2 mb-4">
                <Shield size={20} className="text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">IPL 2026 Teams</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { name: 'Mumbai Indians', short: 'MI', color: 'bg-blue-600' },
                  { name: 'Chennai Super Kings', short: 'CSK', color: 'bg-yellow-500' },
                  { name: 'Royal Challengers', short: 'RCB', color: 'bg-red-600' },
                  { name: 'Kolkata Knight Riders', short: 'KKR', color: 'bg-purple-900' },
                  { name: 'Rajasthan Royals', short: 'RR', color: 'bg-pink-600' },
                  { name: 'Delhi Capitals', short: 'DC', color: 'bg-blue-500' },
                  { name: 'Punjab Kings', short: 'PBKS', color: 'bg-red-500' },
                  { name: 'Sunrisers Hyderabad', short: 'SRH', color: 'bg-orange-500' },
                  { name: 'Gujarat Titans', short: 'GT', color: 'bg-gray-800' },
                  { name: 'Lucknow Super Giants', short: 'LSG', color: 'bg-cyan-600' },
                ].map((team, idx) => (
                  <div key={idx} className="p-3 border border-gray-100 hover:shadow-md transition-all text-center" style={{ borderRadius: '0.75rem' }}>
                    <div className={`w-10 h-10 ${team.color} text-white font-bold flex items-center justify-center mx-auto mb-2`} style={{ borderRadius: '50%' }}>
                      {team.short}
                    </div>
                    <p className="text-xs text-gray-600 font-medium truncate">{team.name}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Event Nudges for Travel */}
            <div className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 shadow" style={{ borderRadius: '1.25rem' }}>
              <div className="flex items-center gap-2 mb-4">
                <Compass size={20} className="text-amber-600" />
                <h3 className="text-lg font-bold text-gray-900">Travel Nudge Opportunities</h3>
              </div>
              <p className="text-gray-600 mb-4">Suggest these IPL destinations to users based on their travel plans:</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-white rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">Weekend Getaways</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Apr 4-5: Mumbai + Ahmedabad double header</li>
                    <li>• Apr 11-12: Chandigarh + Mumbai double header</li>
                    <li>• Apr 25-26: Jaipur + Ahmedabad weekend</li>
                    <li>• May 16-17: Kolkata + Dharamshala weekend</li>
                  </ul>
                </div>
                <div className="p-4 bg-white rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">Hill Station Specials</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• May 11: Dharamshala - PBKS vs DC</li>
                    <li>• May 14: Dharamshala - PBKS vs MI</li>
                    <li>• May 17: Dharamshala - PBKS vs RCB</li>
                    <li>• Perfect for cricket + mountain escape!</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Community Tab */}
        {activeTab === 'community' && communityStats && (
          <div className="space-y-6">
            {/* Community Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Members" value={communityStats.total_members?.toLocaleString() || '0'} icon={Users} color="teal" />
              <StatCard label="Active (30d)" value={communityStats.active_members_30d?.toLocaleString() || '0'} icon={Activity} color="cyan" />
              <StatCard label="Active Posts" value={communityStats.active_posts?.toLocaleString() || '0'} icon={MapPin} color="amber" />
              <StatCard label="Trip Matches" value={communityStats.active_trip_matches?.toLocaleString() || '0'} icon={HandHeart} color="rose" />
            </div>
            
            {/* Post Types & Pro Stats */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Post Breakdown */}
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Post Breakdown</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                        <MapPin size={20} className="text-teal-600" />
                      </div>
                      <span className="text-gray-700">Travel Invites</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">{communityStats.travel_invite_posts || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                        <Megaphone size={20} className="text-amber-600" />
                      </div>
                      <span className="text-gray-700">Broadcasts</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">{communityStats.broadcast_posts || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-rose-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                        <Heart size={20} className="text-rose-600" />
                      </div>
                      <span className="text-gray-700">Join Requests</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">{communityStats.total_join_requests || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                        <HandHeart size={20} className="text-emerald-600" />
                      </div>
                      <span className="text-gray-700">Accepted Matches</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">{communityStats.accepted_requests || 0}</span>
                  </div>
                </div>
              </div>
              
              {/* Pro Membership Stats */}
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Pro Membership</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                        <Crown size={20} className="text-amber-600" />
                      </div>
                      <span className="text-gray-700">Total Pro Members</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">{communityStats.total_pro_members || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                        <TrendingUp size={20} className="text-emerald-600" />
                      </div>
                      <span className="text-gray-700">Upgrades (This Month)</span>
                    </div>
                    <span className="text-xl font-bold text-emerald-600">+{communityStats.pro_upgrades_this_month || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-cyan-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                        <MessageSquare size={20} className="text-cyan-600" />
                      </div>
                      <span className="text-gray-700">Active Chats</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">{communityStats.active_chats || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                        <AlertTriangle size={20} className="text-red-600" />
                      </div>
                      <span className="text-gray-700">Pending Reports</span>
                    </div>
                    <span className={`text-xl font-bold ${communityStats.pending_reports > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {communityStats.pending_reports || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Admin Actions */}
            <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1.25rem' }}>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Community Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button className="flex items-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 transition-all" style={{ borderRadius: '9999px' }}>
                  <Download size={18} />
                  Export Posts
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 transition-all" style={{ borderRadius: '9999px' }}>
                  <AlertTriangle size={18} />
                  Review Reports
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 bg-red-100 text-red-700 font-semibold hover:bg-red-200 transition-all" style={{ borderRadius: '9999px' }}>
                  <Shield size={18} />
                  Disable Pro Chat (Global)
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users by name or email..."
                className="w-full max-w-md pl-12 pr-4 py-3 bg-white border-2 border-gray-100 text-gray-900 placeholder-gray-400 focus:border-slate-400 outline-none transition-all"
                style={{ borderRadius: '9999px' }}
                data-testid="search-users"
              />
            </div>
            
            {/* Users Table */}
            <div className="bg-white/80 backdrop-blur border border-gray-100 shadow overflow-hidden" style={{ borderRadius: '1.25rem' }}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">User</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Email</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Tier</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Plans</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white font-bold" style={{ borderRadius: '50%' }}>
                              {user.name?.charAt(0) || '?'}
                            </div>
                            <span className="font-medium text-gray-900">{user.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{user.email}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 text-xs font-bold capitalize ${
                            user.tier === 'premium' ? 'bg-amber-100 text-amber-700' :
                            user.tier === 'pro' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600'
                          }`} style={{ borderRadius: '9999px' }}>
                            {user.tier}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{user.plans_count}</td>
                        <td className="px-6 py-4 text-gray-500 text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {filteredUsers.length === 0 && (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No users found</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
